// texe-web/src/components/CommentLikeButton.tsx
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import { useAuth } from "../context/AuthProvider";

/**
 * Yorum beğeni butonu.
 * - Login ise hem count hem liked durumunu getirir.
 * - Login değilse sadece sayıyı gösterir; tıklayınca login sayfasına yönlendirir.
 * - Hataları kullanıcıya görünür etmiyor; optimistik güncelleme yapıyor.
 */
export default function CommentLikeButton({ commentId }: { commentId: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [count, setCount] = useState<number>(0);
  const [liked, setLiked] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();

    (async () => {
      try {
        if (user) {
          const res = await apiGet<{ ok: true; count: number; liked: boolean }>(
            `/api/likes/comment/state/${commentId}`
          );
          if (!cancelled) {
            setCount(res.count);
            setLiked(!!res.liked);
          }
        } else {
          const res = await apiGet<{ ok: true; count: number }>(
            `/api/comments/${commentId}/likes`
          );
          if (!cancelled) {
            setCount(res.count);
            setLiked(null);
          }
        }
      } catch {
        // sessiz geç
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [commentId, user]);

  function goLogin() {
    const next = location.pathname + location.search + location.hash;
    navigate(`/login?next=${encodeURIComponent(next)}`);
  }

  async function toggle() {
    if (busy) return;

    if (!user) {
      goLogin();
      return;
    }

    const prevLiked = liked === true;
    const prevCount = count;
    const nextLiked = !prevLiked;
    const nextCount = Math.max(0, prevCount + (nextLiked ? 1 : -1));

    setBusy(true);
    setLiked(nextLiked);
    setCount(nextCount);

    try {
      const res = await apiPost<{ ok: true; liked: boolean }>(
        `/api/comments/${commentId}/like`,
        {}
      );

      if (res.liked !== nextLiked) {
        setLiked(!!res.liked);
        setCount(Math.max(0, prevCount + (res.liked ? 1 : -1)));
      }
    } catch {
      // rollback
      setLiked(prevLiked);
      setCount(prevCount);
    } finally {
      setBusy(false);
    }
  }

  const isLiked = !!liked;
  const icon = isLiked ? "💙" : "🤍";
  const title = user ? (isLiked ? "Beğenmekten vazgeç" : "Beğen") : "Giriş yap ve beğen";

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <button
        onClick={toggle}
        disabled={busy}
        title={title}
        aria-label={title}
        aria-pressed={isLiked}
        style={{
          fontSize: 12,
          lineHeight: 1,
          padding: "4px 6px",
          borderRadius: 6,
          border: "1px solid #333",
          background: "transparent",
          cursor: busy ? "not-allowed" : "pointer",
          opacity: busy ? 0.7 : 1,
        }}
      >
        {icon} {count}
      </button>
      {/* Hata mesajını kullanıcıya göstermiyoruz. */}
    </div>
  );
}
